// timeEntryHours.js
// ─────────────────────────────────────────────────────────────────────────
// The one rule that makes "crossing time zones must not pollute hours" true
// by construction: a wall-clock time is ALWAYS converted to an absolute UTC
// instant at the moment it's captured, and durations are ALWAYS the diff of
// two instants. There is deliberately no code path that subtracts two wall
// times — so the time-zone bug is impossible, not merely avoided.
//
// Requires: luxon (npm i luxon)
// ─────────────────────────────────────────────────────────────────────────

import { DateTime } from 'luxon';

/**
 * Convert a wall-clock time entered in a specific IANA zone into an absolute
 * UTC instant (ISO string, safe to store in a Postgres timestamptz).
 *
 * @param {string} isoDate   'YYYY-MM-DD' — the calendar date as the user sees it
 * @param {string} wallTime  'HH:mm'      — the clock time the user typed/picked
 * @param {string} zone      IANA zone, e.g. 'America/Vancouver'
 * @returns {string} UTC ISO instant, e.g. '2025-07-15T15:00:00.000Z'
 */
export const wallToInstant = (isoDate, wallTime, zone) => {
  const dt = DateTime.fromISO(`${isoDate}T${wallTime}`, { zone });
  if (!dt.isValid) {
    throw new Error(`Invalid time "${isoDate} ${wallTime}" in ${zone}: ${dt.invalidReason}`);
  }
  return dt.toUTC().toISO();
};

/**
 * Worked hours between two stored instants. Pure instant-diff — zone-agnostic
 * by design, because both operands are already absolute. A Pacific→Mountain
 * day comes out one hour shorter with zero special-casing; westbound, longer.
 *
 * @param {string} startUtcISO clock-in instant (UTC ISO / timestamptz)
 * @param {string} endUtcISO   clock-out instant (UTC ISO / timestamptz)
 * @returns {number} hours (decimal). Negative means out < in — caller should reject.
 */
export const workedHours = (startUtcISO, endUtcISO) => {
  if (!startUtcISO || !endUtcISO) return null;
  const start = DateTime.fromISO(startUtcISO, { zone: 'utc' });
  const end = DateTime.fromISO(endUtcISO, { zone: 'utc' });
  return parseFloat(end.diff(start, 'hours').hours.toFixed(2));
};

/**
 * Render a stored instant back in the zone it was entered in, with the short
 * zone abbreviation — this is what you show next to every entry so the zone is
 * always visible, e.g. "08:00 PDT".
 *
 * @param {string} utcISO stored instant
 * @param {string} zone   the origin IANA zone for that entry
 * @returns {string} 'HH:mm ZZZ', e.g. '08:00 PDT'
 */
export const formatInOriginZone = (utcISO, zone) => {
  if (!utcISO || !zone) return '—';
  const dt = DateTime.fromISO(utcISO, { zone: 'utc' }).setZone(zone);
  return `${dt.toFormat('HH:mm')} ${dt.toFormat('ZZZZ')}`;
};

/**
 * A day "crosses time zones" when its entries reference more than one distinct
 * IANA zone. That's the trigger for forcing per-entry location confirmation.
 *
 * @param {string[]} zones IANA zones touched during the day (nulls ignored)
 * @returns {boolean}
 */
export const isCrossingDay = (zones) =>
  new Set((zones || []).filter(Boolean)).size > 1;

// IANA zones relevant to BC Interior / northern routes. The point of using
// IANA (not raw UTC offsets) is that these pockets are handled automatically:
//   - America/Vancouver       Pacific, observes DST            (most of BC)
//   - America/Dawson_Creek     Mountain Standard, NO DST        (Peace: Dawson Creek, Chetwynd)
//   - America/Fort_Nelson      Mountain Standard, NO DST        (Fort Nelson)
//   - America/Creston          Mountain Standard, NO DST        (Creston valley)
//   - America/Edmonton         Mountain, observes DST           (Alberta)
// An offset table would get the no-DST pockets wrong twice a year; IANA never does.
export const BC_ZONE_PRESETS = [
  { zone: 'America/Vancouver', label: 'Pacific (most of BC)' },
  { zone: 'America/Dawson_Creek', label: 'Mountain — no DST (Peace region)' },
  { zone: 'America/Fort_Nelson', label: 'Mountain — no DST (Fort Nelson)' },
  { zone: 'America/Creston', label: 'Mountain — no DST (Creston)' },
  { zone: 'America/Edmonton', label: 'Mountain (Alberta)' },
];
